/* import __COLOCATED_TEMPLATE__ from './http-request-editor.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { HTTP_METHODS } from 'embercom/lib/custom-authentication/request-helper';
import { action } from '@ember/object';
import { post } from 'embercom/lib/ajax';
import resolveObjectPath from 'embercom/lib/resolve-object-path';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type Token from 'embercom/models/custom-authentication/token';
import { taskFor } from 'ember-concurrency-ts';
import { isBlank } from '@ember/utils';
// @ts-ignore
import type NotificationsService from 'embercom/services/notifications-service';
//@ts-ignore
import { sanitizeUrl } from '@intercom/pulse/lib/sanitize';

interface Args {
  customAuthenticationToken: Token;
  form: any;
}

export default class HttpRequestEditor extends Component<Args> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  // @ts-ignore
  @service declare notificationsService: NotificationsService;

  @tracked customAuthenticationToken = this.args.customAuthenticationToken;
  @tracked declare testResponse: any;
  @tracked bannerText = '';
  @tracked declare bannerType: 'confirmation' | 'error';
  @tracked declare displayableResponseBody: string | null;
  @tracked selectedPreviewTab = 'headers';
  @tracked templatedParameters: Record<string, string> = {};
  @tracked disableTestButton = true;
  @tracked hasIncompleteHeaders = false;
  @tracked urlBlocks: any[] = [];
  @tracked bodyBlocks: any[] = [];

  constructor(owner: any, args: any) {
    super(owner, args);
    if (this.isTemplatingFeatureEnabled) {
      this.urlBlocks = this.refreshTokenConfigurationURLToBlocks();
      this.bodyBlocks = this.refreshTokenConfigurationBodyToBlocks();
    }
    this.refreshTemplatedParameters();
  }

  @dropTask
  *testRequest() {
    let config = this.customAuthenticationToken.refreshTokenConfiguration;

    // Remove empty headers before sending to prevent backend validation errors
    // Save them so we can restore on failure to prevent data loss
    let emptyHeaders = (config.headers?.toArray() || []).filter(
      (header: { name: string; value: string }) => !header.name && !header.value,
    );
    emptyHeaders.forEach((header: object) => config.headers?.removeFragment(header));

    let params = {
      app_id: this.appService.app.id,
      refresh_token_configuration: config,
    };
    try {
      this._setLatestTestRequestParams();
      config.mostRecentTestPassed = false;
      // @ts-ignore
      this.testResponse = yield post('/ember/custom_authentication/tokens/test', params);
      this.displayableResponseBody = JSON.stringify(this.responseBody, null, 2);
      this._setBannerParams();
    } catch (exception) {
      // Restore empty headers on failure so user doesn't lose their work
      emptyHeaders.forEach((header: object) => config.headers?.pushObject(header));
      this.displayableResponseBody = null;
      this._setBannerOnException(exception);
    }
  }

  get isTestRequestRunning() {
    return taskFor(this.testRequest).isRunning;
  }

  @action refreshTemplatedParameters() {
    this.templatedParameters = {};
    Array.from(
      this.customAuthenticationToken.refreshTokenConfiguration.extractedTemplatedParameters,
    ).map(
      (attribute) =>
        (this.templatedParameters[attribute as string] = this.getValueforTemplatedParameter(
          attribute as string,
        )),
    );
    this._setDisableTestButton();
  }

  getValueforTemplatedParameter(attribute: string) {
    return this.customAuthenticationToken.refreshTokenConfiguration.testValues
      ? this.customAuthenticationToken.refreshTokenConfiguration.testValues[attribute] || ''
      : '';
  }

  @action
  setTemplatedParameterValue(parameter: string, event: any) {
    this.templatedParameters[parameter] = event.target.value;
    this.customAuthenticationToken.refreshTokenConfiguration.testValues = {}; // set to empty first to trigger validation when updated below
    this.customAuthenticationToken.refreshTokenConfiguration.testValues = this.templatedParameters;
    this._setDisableTestButton();
  }

  _setDisableTestButton() {
    let hasBlankTemplatedParams = Object.values(this.templatedParameters).some(isBlank);
    let headers =
      this.customAuthenticationToken.refreshTokenConfiguration?.headers?.toArray() || [];
    this.hasIncompleteHeaders = headers.some((header: { name: string; value: string }) => {
      let hasName = !isBlank(header.name);
      let hasValue = !isBlank(header.value);
      return hasName !== hasValue; // XOR: true when exactly one is filled
    });

    this.disableTestButton = hasBlankTemplatedParams || this.hasIncompleteHeaders;
  }

  _setLatestTestRequestParams() {
    // Note: Empty headers (both name and value blank) are already removed in testRequest()
    // before this method is called, so we use the original headers reference here.
    // Using a filtered copy would break _configsMatch() which compares by reference.
    this.customAuthenticationToken.refreshTokenConfiguration.latestTestRequestParams =
      this.store.createRecord('custom-authentication/token', {
        httpMethod: this.customAuthenticationToken.refreshTokenConfiguration.httpMethod,
        url: this.customAuthenticationToken.refreshTokenConfiguration.url,
        body: this.customAuthenticationToken.refreshTokenConfiguration.body,
        headers: this.customAuthenticationToken.refreshTokenConfiguration.headers,
        testValues: this.customAuthenticationToken.refreshTokenConfiguration.testValues,
      });
  }

  _setBannerParams() {
    this.bannerText = '';
    if (this.testResponse.refresh_response.success) {
      this.customAuthenticationToken.refreshTokenConfiguration.mostRecentTestPassed = true;
      this.bannerType = 'confirmation';
      this.bannerText += `${this.testResponse.refresh_response.status} ${this.testResponse.refresh_response.return_code.toUpperCase()}`;
    } else {
      this.customAuthenticationToken.refreshTokenConfiguration.mostRecentTestPassed = false;
      this.bannerType = 'error';
      this.bannerText +=
        this.testResponse.refresh_response.human_readable_error ||
        this.intl.t('settings.custom-authentication-tokens.new.response.error');
    }
  }

  _setBannerOnException(exception: any) {
    this.bannerType = 'error';
    this.bannerText = exception.errorThrown;
  }

  get responseBody() {
    let response = this.testResponse?.refresh_response;
    if (response?.body) {
      return response.body;
    }
    if (!response?.raw_body) {
      return null;
    }
    try {
      return JSON.parse(response.raw_body);
    } catch {
      return response.raw_body;
    }
  }

  get supportedHttpMethods() {
    return HTTP_METHODS.filter((method) => method.value !== 'delete');
  }

  get isBodyAvailable() {
    if (!this.customAuthenticationToken.refreshTokenConfiguration) {
      return false;
    }
    return ['post', 'put'].includes(
      this.customAuthenticationToken.refreshTokenConfiguration.httpMethod,
    );
  }

  get responsePaths() {
    let items = this.testResponse ? this.pathItemsFromResponse : [];
    if (this.customAuthenticationToken.refreshTokenConfiguration.tokenResponsePath) {
      items.pushObject(this.tokenPathItem);
    }
    return [
      {
        items: items.uniqBy('value'),
      },
    ];
  }

  get tokenPathItem() {
    return {
      value: this.customAuthenticationToken.refreshTokenConfiguration.tokenResponsePath,
      text: this.customAuthenticationToken.refreshTokenConfiguration.tokenResponsePath,
    };
  }

  get pathItemsFromResponse() {
    return this.testResponse.possible_response_paths.map((path: string) => {
      return {
        value: path,
        text: path,
      };
    });
  }

  @action
  setPathAndTokenValue(path: string) {
    this.customAuthenticationToken.refreshTokenConfiguration.tokenResponsePath = path;
    let tokenValue;
    if (path.startsWith('headers.')) {
      let headerKey = path.replace('headers.', '');
      tokenValue = this.testResponse?.refresh_response?.headers?.[headerKey];
    } else if (path.startsWith('body.')) {
      let bodyPath = path.replace('body.', '');
      tokenValue = resolveObjectPath(this.responseBody, bodyPath);
    } else {
      tokenValue = resolveObjectPath(this.responseBody, path);
    }
    this.customAuthenticationToken.tokenValue = tokenValue;
  }

  @action
  setRefreshConfigHttpMethod(httpMethod: string) {
    this.customAuthenticationToken.refreshTokenConfiguration.httpMethod = httpMethod;
    if (!this.isBodyAvailable) {
      this.selectedPreviewTab = 'headers';
      this.customAuthenticationToken.refreshTokenConfiguration.body = '';
      this.bodyBlocks = this.refreshTokenConfigurationBodyToBlocks();
      this.refreshTemplatedParameters();
    }
  }

  @action
  changePreviewTab(selectedTab: string) {
    this.selectedPreviewTab = selectedTab;
  }

  get refreshTokenUrlValidation() {
    return this.customAuthenticationToken.refreshTokenConfiguration.validations.attrs.url;
  }

  get isTemplatingFeatureEnabled() {
    return this.appService.app.canUseFeature('custom-authentication-tokens-templating');
  }

  refreshTokenConfigurationURLToBlocks() {
    return [
      {
        type: 'paragraph',
        text: this.customAuthenticationToken.refreshTokenConfiguration.url || '',
        modelKey: 'common/blocks/paragraph',
      },
    ];
  }

  refreshTokenConfigurationBodyToBlocks() {
    if (!this.customAuthenticationToken.refreshTokenConfiguration.body) {
      return [];
    }

    let bodyLines = this.customAuthenticationToken.refreshTokenConfiguration.body.split('\n');
    let bodyBlocks = bodyLines.map((line: string) => {
      return {
        type: 'paragraph',
        text: line,
        class: 'no-margin',
        modelKey: 'common/blocks/paragraph',
      };
    });
    return bodyBlocks;
  }

  @action
  onUrlComposerChange(newBlocksDoc: { blocks: { text: string }[] }) {
    if (this.isTemplatingFeatureEnabled) {
      let blocks = newBlocksDoc.blocks;
      let processedBlocks = sanitizeUrl(blocks[0].text);
      this.customAuthenticationToken.refreshTokenConfiguration.url = processedBlocks.toString();
      this.refreshTemplatedParameters();
    }
  }

  @action
  onBodyComposerChange(newBlocksDoc: { blocks: { text: string }[] }) {
    if (this.isTemplatingFeatureEnabled) {
      let blocks = newBlocksDoc.blocks;
      let blockTexts = blocks.map((block) => block.text);
      let processedBlocks = blockTexts.join('\n');
      this.customAuthenticationToken.refreshTokenConfiguration.body = processedBlocks.toString();
      this.refreshTemplatedParameters();
    }
  }

  get localAttributes() {
    return [];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::CustomAuthentication::HttpRequestEditor': typeof HttpRequestEditor;
    'settings/custom-authentication/http-request-editor': typeof HttpRequestEditor;
  }
}
